// ============================================
// USER PROFILE
// ============================================

import { supabase } from '../../lib/supabase';
import type { UserProfile } from '../types';
import { requireUserId, throwWithContext, notify } from './_shared';

/* eslint-disable @typescript-eslint/no-explicit-any */

export async function fetchProfile(): Promise<UserProfile | null> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error || !data) return null;
  return mapProfileFromDb(data);
}

export async function updateProfile(updates: Partial<UserProfile>): Promise<void> {
  const userId = await requireUserId();
  const dbUpdates = mapProfileToDb(updates);
  const { error } = await supabase
    .from('user_profiles')
    .update(dbUpdates)
    .eq('id', userId);
  if (error) throwWithContext("Couldn't update your profile", error);
  notify('user_profiles');
}

/**
 * Flip has_seen_tour=true on the current user's profile. Called when the
 * guided dashboard tour completes OR is skipped - both paths permanently
 * dismiss the tour. has_seen_tour is NOT in the protected-columns list
 * of guard_user_profiles_protected_columns, so standard RLS lets the
 * user update their own row directly without an edge function. Errors
 * here are non-fatal (the tour ran; the persistence is cosmetic).
 * Mirrors the addProtocol flag-flip pattern.
 */
export async function markTourSeen(): Promise<void> {
  try {
    const userId = await requireUserId();
    await supabase
      .from('user_profiles')
      .update({
        has_seen_tour: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);
    notify('user_profiles');
  } catch (err) {
    console.warn('[cloud] could not flip has_seen_tour flag:', err);
  }
}

/**
 * Stamp the medical/legal disclaimer acknowledgment on the caller's
 * profile. Routed through the acknowledge-disclaimer edge function
 * because the guard trigger installed by migration
 * 20260423_user_profiles_column_guard.sql blocks client writes to
 * disclaimer_acknowledged_at, has_accepted_disclaimer,
 * accepted_disclaimer_at, and accepted_terms_at. Returns once the
 * edge function confirms the row was updated.
 */
export async function acknowledgeDisclaimer(): Promise<void> {
  const { data, error } = await supabase.functions.invoke('acknowledge-disclaimer', {
    method: 'POST',
  });
  if (error) {
    throwWithContext("Couldn't save your disclaimer acknowledgment", error);
  }
  if (data && typeof data === 'object' && 'error' in data) {
    throw new Error((data as { error: string; message?: string }).message ?? 'Disclaimer acknowledgment failed.');
  }
  notify('user_profiles');
}

/**
 * Deletes the current user's account + all personal data (Apple Guideline
 * 5.1.1(v)). Calls the `delete-account` edge function which authenticates
 * via the user's JWT, uses service_role to cascade-delete everything, and
 * also sweeps the pre-v2 backup tables.
 *
 * Returns on success. Caller is expected to immediately sign out + route
 * the user away from authenticated surfaces.
 */
export async function deleteOwnAccount(): Promise<void> {
  const { data, error } = await supabase.functions.invoke('delete-account', {
    method: 'POST',
  });
  if (error) {
    throwWithContext("Couldn't delete your account", error);
  }
  if (data && typeof data === 'object' && 'error' in data) {
    throw new Error((data as { error: string; message?: string }).message ?? 'Account deletion failed.');
  }
}

/**
 * Fire-and-forget the new-account welcome email. Invoked from the auth
 * bootstrap layer (AuthProvider) on SIGNED_IN, which fires for every provider
 * (email OTP, Apple, Google). The send-welcome-email edge function derives the
 * email/name from the caller's JWT and claims welcome_email_sent_at on the
 * profile atomically, so this is safe to call more than once: the server only
 * ever sends the first time. Existing users are grandfathered by the
 * 20260715_welcome_email.sql backfill, so returning sign-ins are a no-op send.
 *
 * We also keep an in-memory guard so a single app session never re-invokes for
 * the same user (supabase-js can emit SIGNED_IN more than once). Errors are
 * swallowed: a welcome email must never disrupt sign-in.
 */
const welcomeAttempted = new Set<string>();

export function maybeSendWelcomeEmail(userId: string): void {
  if (!userId || welcomeAttempted.has(userId)) return;
  welcomeAttempted.add(userId);
  try {
    void supabase.functions
      .invoke('send-welcome-email', { body: { type: 'signup' } })
      .catch((err) => {
        console.warn('[welcome-email] signup invoke failed (non-fatal):', err);
      });
  } catch (err) {
    console.warn('[welcome-email] signup invoke threw (non-fatal):', err);
  }
}

// ============================================
// Field Mapping: camelCase ↔ snake_case
// ============================================

function mapProfileFromDb(row: any): UserProfile {
  return {
    id: row.id,
    displayName: row.display_name ?? undefined,
    hasCompletedOnboarding: row.has_completed_onboarding,
    userType: row.user_type ?? undefined,
    hasAcceptedDisclaimer: row.has_accepted_disclaimer,
    notificationsEnabled: row.notifications_enabled,
    measurementUnit: row.measurement_unit,
    darkMode: row.dark_mode,
    acceptedTermsAt: row.accepted_terms_at,
    acceptedDisclaimerAt: row.accepted_disclaimer_at,
    disclaimerAcknowledgedAt: row.disclaimer_acknowledged_at,
    subscriptionStatus: row.subscription_status ?? 'none',
    subscriptionStripeCustomerId: row.subscription_stripe_customer_id,
    subscriptionStripeSubscriptionId: row.subscription_stripe_subscription_id,
    inviteCodeUsed: row.invite_code_used,
    hasCreatedFirstProtocol: row.has_created_first_protocol ?? false,
    hasSeenTour: row.has_seen_tour ?? false,
    subscriptionIntent: row.subscription_intent ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// Whitelist of columns the client is allowed to update directly on
// user_profiles. Subscription state, disclaimer timestamps, terms
// acceptance, invite-code usage, and identity/audit columns are all
// owned by server-side flows (Stripe webhook, RevenueCat webhook,
// acknowledge-disclaimer edge function). The database-level trigger
// installed by migration 20260423_user_profiles_column_guard.sql
// rejects client writes to those columns with SQLSTATE 42501;
// filtering here is belt-and-suspenders so we never even try.
function mapProfileToDb(p: Partial<UserProfile>): Record<string, unknown> {
  const m: Record<string, unknown> = {};
  if (p.displayName !== undefined) m.display_name = p.displayName;
  if (p.hasCompletedOnboarding !== undefined) m.has_completed_onboarding = p.hasCompletedOnboarding;
  if (p.userType !== undefined) m.user_type = p.userType;
  if (p.notificationsEnabled !== undefined) m.notifications_enabled = p.notificationsEnabled;
  if (p.measurementUnit !== undefined) m.measurement_unit = p.measurementUnit;
  if (p.darkMode !== undefined) m.dark_mode = p.darkMode;
  if (p.hasCreatedFirstProtocol !== undefined) m.has_created_first_protocol = p.hasCreatedFirstProtocol;
  if (p.hasSeenTour !== undefined) m.has_seen_tour = p.hasSeenTour;
  // subscription_intent is client-writable (not in the protected-columns
  // guard). Captured at onboarding, persisted once on first dashboard load.
  if (p.subscriptionIntent !== undefined) m.subscription_intent = p.subscriptionIntent;
  m.updated_at = new Date().toISOString();
  return m;
}
